import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Info as AboutIcon,
  Rule as RuleIcon,
  PhotoLibrary as GalleryIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  AdminPanelSettings as AdminIcon,
  Groups as GroupsIcon,
  CalendarMonth as CalendarIcon,
  CardMembership as MembershipIcon,
  EmojiEvents as HonourRollIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../services/AuthService';

// Get the appropriate admin landing page based on user role
const getAdminLandingPage = (role?: UserRole): string => {
  switch (role) {
    case 'club_admin':
      return '/admin/target-orders';
    case 'regional_admin':
    case 'event_manager':
      // These roles don't have dashboard access, redirect to calendar
      return '/admin/calendar';
    case 'event_staff':
      // Event staff only have access to events they're assigned to
      return '/admin/events';
    case 'super_admin':
    case 'members_admin':
    default:
      // These roles have full access, go to dashboard
      return '/admin';
  }
};

const navigationItems = [
  { label: 'Home', path: '/', icon: <HomeIcon /> },
  { label: 'About', path: '/about', icon: <AboutIcon /> },
  { label: 'Council', path: '/council', icon: <GroupsIcon /> },
  { label: 'Membership', path: '/membership', icon: <MembershipIcon /> },
  { label: 'Calendar', path: '/calendar', icon: <CalendarIcon /> },
  { label: 'Honour Roll', path: '/honour-roll', icon: <HonourRollIcon /> },
  // { label: 'Events', path: '/events', icon: <EventIcon /> },
  { label: 'Rules', path: '/rules', icon: <RuleIcon /> },
  { label: 'Gallery', path: '/gallery', icon: <GalleryIcon /> },
];

export const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  // Use lg (1200px) so phones requesting "desktop site" (~980px) still get the
  // hamburger + always-visible Logout instead of a clipped desktop nav row.
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, isAdmin, logout, loading } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      handleMenuClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const drawer = (
    <Box sx={{ width: 250 }}>
      <List>
        {navigationItems.map((item) => (
          <ListItem
            button
            key={item.path}
            onClick={() => handleNavigation(item.path)}
            selected={isActive(item.path)}
            sx={{
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isActive(item.path) ? theme.palette.primary.contrastText : 'inherit',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        
        {loading ? (
          <>
            <Divider />
            <ListItem disabled>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Loading..." />
            </ListItem>
          </>
        ) : currentUser ? (
          <>
            <Divider />
            <ListItem
              button
              onClick={() => handleNavigation('/dashboard')}
              selected={isActive('/dashboard') || isActive('/profile') || isActive('/documents')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: (isActive('/dashboard') || isActive('/profile') || isActive('/documents')) ? theme.palette.primary.contrastText : 'inherit',
                }}
              >
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="My Account" />
            </ListItem>
            {isAdmin && (
              <ListItem
                button
                onClick={() => handleNavigation(getAdminLandingPage(userProfile?.role))}
                selected={location.pathname.startsWith('/admin')}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname.startsWith('/admin') ? theme.palette.primary.contrastText : 'inherit',
                  }}
                >
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText primary="Admin" />
              </ListItem>
            )}
            <ListItem
              button
              onClick={() => handleNavigation('/rules')}
              selected={isActive('/rules')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive('/rules') ? theme.palette.primary.contrastText : 'inherit',
                }}
              >
                <RuleIcon />
              </ListItemIcon>
              <ListItemText primary="Rules" />
            </ListItem>
            <ListItem
              button
              onClick={() => handleNavigation('/gallery')}
              selected={isActive('/gallery')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive('/gallery') ? theme.palette.primary.contrastText : 'inherit',
                }}
              >
                <GalleryIcon />
              </ListItemIcon>
              <ListItemText primary="Gallery" />
            </ListItem>
            <ListItem
              button
              onClick={handleLogout}
            >
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </>
        ) : (
          <>
            <Divider />
            <ListItem
              button
              onClick={() => handleNavigation('/login')}
              selected={isActive('/login')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive('/login') ? theme.palette.primary.contrastText : 'inherit',
                }}
              >
                <LoginIcon />
              </ListItemIcon>
              <ListItemText primary="Login" />
            </ListItem>
            <ListItem
              button
              onClick={() => handleNavigation('/signup')}
              selected={isActive('/signup')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive('/signup') ? theme.palette.primary.contrastText : 'inherit',
                }}
              >
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Membership Sign Up" />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ pl: 'max(8px, env(safe-area-inset-left))' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { lg: 'none' }, flexShrink: 0 }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Logo and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
            <Box
              component="img"
              src="/images/SAGRF_Logo.jpg"
              alt="SAGRF Logo"
              sx={{
                maxHeight: { xs: 40, sm: 50, md: 60 },
                maxWidth: { xs: 60, sm: 75, md: 90 },
                width: 'auto',
                height: 'auto',
                mr: 2,
                borderRadius: 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
            <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
              <Typography
                variant="h6"
                component="div"
                sx={{ 
                  fontWeight: 700,
                  fontSize: { sm: '1.1rem', md: '1.25rem' }
                }}
              >
                SAGRF
              </Typography>
              <Typography
                variant="caption"
                sx={{ 
                  display: 'block',
                  fontSize: { sm: '0.7rem', md: '0.8rem' },
                  opacity: 0.9,
                  lineHeight: 1.2
                }}
              >
                South African Gallery Rifle Federation
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation (lg and up only) */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    backgroundColor: isActive(item.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.2)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              
              {/* Authentication Section */}
              {loading ? (
                // Show loading state for authentication buttons
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    color="inherit"
                    disabled
                    sx={{ opacity: 0.7 }}
                  >
                    Loading...
                  </Button>
                </Box>
              ) : currentUser ? (
                <>
                  <Button
                    color="inherit"
                    onClick={() => handleNavigation('/dashboard')}
                    sx={{
                      backgroundColor: isActive('/dashboard') || isActive('/profile') || isActive('/documents') ? 'rgba(255,255,255,0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.2)',
                      },
                    }}
                  >
                    My Account
                  </Button>
                  {isAdmin && (
                    <Button
                      color="inherit"
                      onClick={() => handleNavigation(getAdminLandingPage(userProfile?.role))}
                      sx={{
                        backgroundColor: location.pathname.startsWith('/admin') ? 'rgba(255,255,255,0.1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.2)',
                        },
                      }}
                    >
                      Admin
                    </Button>
                  )}
                  <IconButton
                    color="inherit"
                    onClick={handleMenuOpen}
                    sx={{ ml: 1 }}
                  >
                    <Avatar 
                      src={userProfile?.profilePictureURL || undefined}
                      sx={{ width: 32, height: 32 }}
                    >
                      {!userProfile?.profilePictureURL && (
                        userProfile?.preferredName && userProfile?.lastName
                          ? `${userProfile.preferredName[0]}${userProfile.lastName[0]}`.toUpperCase()
                          : <PersonIcon />
                      )}
                    </Avatar>
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    color="inherit"
                    onClick={() => handleNavigation('/login')}
                    startIcon={<LoginIcon />}
                    sx={{
                      backgroundColor: isActive('/login') ? 'rgba(255,255,255,0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.2)',
                      },
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => handleNavigation('/signup')}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.5)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.8)',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    Membership Sign Up
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* Always-visible Logout so a dead/clipped session can still be cleared */}
          {currentUser && (
            <IconButton
              color="inherit"
              aria-label="Logout"
              onClick={handleLogout}
              sx={{ ml: 0.5, flexShrink: 0 }}
            >
              <LogoutIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        disableRestoreFocus
        disableAutoFocus
      >
        <MenuItem onClick={() => handleNavigation('/dashboard')}>
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          My Account
        </MenuItem>
        {isAdmin && (
          <MenuItem onClick={() => handleNavigation(getAdminLandingPage(userProfile?.role))}>
            <ListItemIcon>
              <AdminIcon fontSize="small" />
            </ListItemIcon>
            Admin Panel
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleNavigation('/about')}>
          <ListItemIcon>
            <AboutIcon fontSize="small" />
          </ListItemIcon>
          About
        </MenuItem>
        <MenuItem onClick={() => handleNavigation('/rules')}>
          <ListItemIcon>
            <RuleIcon fontSize="small" />
          </ListItemIcon>
          Rules
        </MenuItem>
        <MenuItem onClick={() => handleNavigation('/gallery')}>
          <ListItemIcon>
            <GalleryIcon fontSize="small" />
          </ListItemIcon>
          Gallery
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
}; 